import React, { useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AdminCompanyDetailOutDTO, CompanyDetailDuplicateCheckOutDTO, IdEntityTypeEnum, LovDTO } from '@api/ringil-api.v2';
import { getCrnFormatDescription, validateCrn } from '@utils/crnValidation';
import { isNotNullOrUndefined } from '@utils/utils';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import AdminModulesList from '../modules/AdminModulesList';
import AdminIhneritedModules from '@components/pages/admin/modules/AdminIhneritedModules';
import AdminCompanyUsers from '@components/pages/admin/users/AdminCompanyUsers';
import AdminCompanyInheriters from '@components/pages/admin/AdminCompanyInheriters/AdminCompanyInheriters';
import AdminCompanyFolders from '@components/pages/admin/folder/AdminCompanyFolders.tsx';
import BetaFeaturesSection, { BetaFeaturesSectionHandle } from '@components/pages/admin/companies/BetaFeaturesSection.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { ConflictErrors, DuplicateField, Form, normalizeOptionalString } from '@components/pages/admin/companies/adminCompanyDetailForm.ts';
import { adminCompanyDetailToForm, adminCompanyUpdateRequest } from '@components/pages/admin/companies/adminCompanyDetailMapping.ts';
import CompanySection from '@components/pages/admin/companies/sections/CompanySection.tsx';
import PrefixesSection from '@components/pages/admin/companies/sections/PrefixesSection.tsx';
import FeaturesSection from '@components/pages/admin/companies/sections/FeaturesSection.tsx';
import GeneralSection from '@components/pages/admin/companies/sections/GeneralSection.tsx';
import InvoiceSection from '@components/pages/admin/companies/sections/InvoiceSection.tsx';
import NotificationCenterSection from '@components/pages/admin/companies/sections/NotificationCenterSection.tsx';
import ReminderSettingsSection from '@components/pages/admin/companies/sections/ReminderSettingsSection.tsx';
import SvgBuilding from '@icons/building.svg?react';
import SvgList from '@icons/list.svg?react';
import SvgCog from '@icons/cog.svg?react';
import SvgMoney from '@icons/money.svg?react';
import SvgBox from '@icons/box.svg?react';
import SvgUser from '@icons/user-filled.svg?react';
import SvgFolder from '@icons/folder.svg?react';
import SvgBell from '@icons/bell.svg?react';
import SvgFlag from '@icons/flag.svg?react';
import SvgClock from '@icons/Clock.svg?react';

interface ComponentProps {
  companyId: string;
}




const AdminCompanyDetailPage: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const {
    adminGetCompanyDetail,
    adminUpdateCompanyDetail,
    checkCompanyDetailDuplicates,
    adminLovAllSpecialReqs,
    adminLovCargoTemplates,
    adminLovExternalIdTypes,
  } = useContext(ApiContext);
  const [resetData, setResetData] = useState<Form>();
  const [allReqs, setAllReqs] = useState<{ label: string; value: string }[]>();
  const [cargoTemplates, setCargoTemplates] = useState<LovDTO[]>();
  const [data, setData] = useState<AdminCompanyDetailOutDTO>();
  const [externalIdTypes, setExternalIdTypes] = useState<{ label: string; value: string }[]>([]);
  const [conflictErrors, setConflictErrors] = useState<ConflictErrors>({});
  const [saving, setSaving] = useState(false);
  const betaFeaturesRef = useRef<BetaFeaturesSectionHandle>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [bypassCrnValidation, setBypassCrnValidation] = useState(false);
  const [bypassTinValidation, setBypassTinValidation] = useState(false);
  const { showToastMessage } = useContext(ToastContext);

  const form = useForm<Form>(yup.object().shape({}), null, d => save(d), false, false);

  const names = proxiedPropertiesOf<Form>();

  const setEntityPrefix = (idEntityType: IdEntityTypeEnum, prefix: string) => {
    const next = (form.values.entityPrefixes ?? []).map(p => (p.idEntityType === idEntityType ? { ...p, prefix } : p));
    form.setFieldValue(names.entityPrefixes, next);
  };

  const reset = () => {
    form.setValues(resetData);
  };

  const hasFieldChanged = (field: DuplicateField, value?: string | null) => {
    if (!resetData) {
      return true;
    }

    const originalValue = field === 'crn' ? resetData.crn : resetData.tin;
    return normalizeOptionalString(originalValue) !== normalizeOptionalString(value);
  };

  const setDuplicateErrors = (duplicateCheck: CompanyDetailDuplicateCheckOutDTO, tinValue?: string | null, fields: DuplicateField[] = ['crn', 'tin']) => {
    const nextErrors: ConflictErrors = {};

    if (fields.includes('crn')) {
      nextErrors.crn = duplicateCheck.crnDuplicate ? tr('adminCompanyDetail.crnConflict', 'CRN already in use by another company') : undefined;
    }
    if (fields.includes('tin')) {
      nextErrors.tin = duplicateCheck.tinDuplicate && normalizeOptionalString(tinValue)
        ? tr('adminCompanyDetail.tinConflict', 'TIN already in use by another company')
        : undefined;
    }

    setConflictErrors(prev => ({
      ...prev,
      ...nextErrors,
    }));

    return fields.some(field => Boolean(nextErrors[field]));
  };

  const getCrnFormatError = (crn?: string | null) => {
    if (bypassCrnValidation) {
      return undefined;
    }
    const normalizedCrn = crn?.trim();
    if (!normalizedCrn || !data?.country) {
      return undefined;
    }

    const isValid = validateCrn(normalizedCrn, data.country);
    if (isValid) {
      return undefined;
    }

    const formatDesc = getCrnFormatDescription(data.country, tr);
    return formatDesc
      ? tr('validationCrn.crnInvalidFormat', 'Invalid CRN format (expected format: {{format}})', { format: formatDesc })
      : tr('validationCrn.crnInvalid', 'Invalid CRN format');
  };

  const checkDuplicates = (
    values: Pick<Form, 'crn' | 'tin'>,
    fields: DuplicateField[],
    onNoDuplicates?: () => void,
    onDuplicates?: () => void,
    onValidationError?: () => void,
    onError?: () => void,
  ) => {
    const normalizedCrn = values.crn?.trim();
    const normalizedTin = normalizeOptionalString(values.tin);
    const crnFormatError = fields.includes('crn') ? getCrnFormatError(values.crn) : undefined;

    if (crnFormatError) {
      setConflictErrors(prev => ({
        ...prev,
        crn: crnFormatError,
      }));
      onValidationError?.();
      return;
    }

    if ((!fields.includes('crn') || !normalizedCrn) && (!fields.includes('tin') || !normalizedTin)) {
      setConflictErrors(prev => ({
        ...prev,
        ...(fields.includes('crn') ? { crn: undefined } : {}),
        ...(fields.includes('tin') ? { tin: undefined } : {}),
      }));
      onNoDuplicates?.();
      return;
    }

    checkCompanyDetailDuplicates(
      companyId,
      {
        crn: normalizedCrn,
        tin: normalizedTin,
      },
      duplicateCheck => {
        if (!setDuplicateErrors(duplicateCheck, values.tin, fields)) {
          onNoDuplicates?.();
        } else {
          onDuplicates?.();
        }
      },
      undefined,
      {
        onError: () => onError?.(),
      },
    );
  };

  useEffect(() => {
    if (isNotNullOrUndefined(companyId)) {
      load(companyId);
    }
    adminLovAllSpecialReqs(d => {
      setAllReqs(d.map(v => ({ label: v, value: v })));
    });
    adminLovCargoTemplates(data => setCargoTemplates(data));
    adminLovExternalIdTypes(data => setExternalIdTypes(data.map(v => ({ label: v, value: v }))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const load = (companyId: string) => {
    adminGetCompanyDetail(companyId, d => {
      setData(d);
      setConflictErrors({});
      const rstData = adminCompanyDetailToForm(d);
      form.setValues(rstData);
      setResetData(rstData);
    });
  };

   const save = (d: Form) => {
    setSaving(true);
    const fieldsToCheck: DuplicateField[] = bypassTinValidation ? ['crn'] : ['crn', 'tin'];
    checkDuplicates(
      {
        crn: d.crn,
        tin: bypassTinValidation ? undefined : d.tin,
      },
      fieldsToCheck,
      () => {
        adminUpdateCompanyDetail(
          companyId,
          adminCompanyUpdateRequest(d, bypassTinValidation),
          () => {
            // Beta features live on a separate endpoint; the single page Save fires both, and the
            // section only PUTs when its rows actually changed.
            betaFeaturesRef.current?.saveIfChanged(() => {
              setSaving(false);
              setConflictErrors({});
              showToastMessage(tr('generic.saved', 'Saved'), tr('adminCompanyDetail.saved', 'Company detail saved'), 'success');
              load(companyId);
            });
          },
          undefined,
          {
            onConflict: (conflictResponse?: { crnConflictingCompanyId?: string; tinConflictingCompanyId?: string }) => {
              setSaving(false);
              const newErrors: ConflictErrors = {};

              if (conflictResponse?.crnConflictingCompanyId) {
                newErrors.crn = tr('adminCompanyDetail.crnConflict', 'CRN already in use by another company');
              }
              if (conflictResponse?.tinConflictingCompanyId && normalizeOptionalString(form.values.tin)) {
                newErrors.tin = tr('adminCompanyDetail.tinConflict', 'TIN already in use by another company');
              }
              setConflictErrors(newErrors);
              showToastMessage(
                tr('generic.error', 'Error'),
                tr('adminCompanyDetail.resolveDuplicatesBeforeSave', 'Please resolve duplicate CRN/TIN values before saving'),
                'error',
              );
            },
            onError: () => {
              setSaving(false);
              showToastMessage(
                tr('generic.error', 'Error'),
                tr('adminCompanyDetail.saveFailed', 'Failed to save company detail'),
                'error',
              );
            },
          },
        );
      },
      () => {
        setSaving(false);
        showToastMessage(
          tr('generic.error', 'Error'),
          tr('adminCompanyDetail.resolveDuplicatesBeforeSave', 'Please resolve duplicate CRN/TIN values before saving'),
          'error',
        );
      },
      () => {
        setSaving(false);
        showToastMessage(
          tr('generic.error', 'Error'),
          tr('adminCompanyDetail.resolveCrnFormatBeforeSave', 'Please fix CRN format before saving'),
          'error',
        );
      },
      () => {
        setSaving(false);
        showToastMessage(
          tr('generic.error', 'Error'),
          tr('adminCompanyDetail.duplicateValidationFailed', 'Failed to validate CRN/TIN duplicates'),
          'error',
        );
      },
    );
  };

  const tabs: TabProps[] = [
    { label: tr('generic.company', 'Company'), active: activeTab === 0, anchorId: 'admin-company-company', icon: <SvgBuilding /> },
    { label: tr('adminCompanyDetail.sectionFeatures', 'Features'), active: activeTab === 1, anchorId: 'admin-company-features', icon: <SvgList /> },
    { label: tr('adminCompanyDetail.sectionGeneral', 'General'), active: activeTab === 2, anchorId: 'admin-company-general', icon: <SvgCog /> },
    { label: tr('adminCompanyDetail.sectionInvoice', 'Invoice'), active: activeTab === 3, anchorId: 'admin-company-invoice', icon: <SvgMoney /> },
    { label: tr('adminCompanyDetail.sectionModules', 'Modules'), active: activeTab === 4, anchorId: 'admin-company-modules', icon: <SvgBox /> },
    { label: tr('adminCompanyDetail.sectionUsers', 'Users'), active: activeTab === 5, anchorId: 'admin-company-users', icon: <SvgUser /> },
    { label: tr('adminCompanyDetail.sectionFolders', 'Folders'), active: activeTab === 6, anchorId: 'admin-company-folders', icon: <SvgFolder /> },
    { label: tr('adminCompanyDetail.sectionTools', 'Tools'), active: activeTab === 7, anchorId: 'admin-company-tools', icon: <SvgBell /> },
    { label: tr('adminCompanyDetail.sectionReminders', 'Reminders'), active: activeTab === 8, anchorId: 'admin-company-reminders', icon: <SvgClock /> },
  ];

  return (
    <>
      {form.values && data && (
        <div className={'flex flex-col gap-4'}>
          <BreadCrumbComponent items={[{ label: 'Companies', url: '/admin/companies' }, { label: 'Company detail' }]} />
          <Heading
            title={form.values.name}
            headingVariant={'h1'}
            buttons={[
              <RingilButton
                key={'admin-company-save'}
                mode={'filled'}
                label={tr('generic.save', 'Save')}
                onClick={() => form.validateAndSend()}
                disabled={saving}
                dataTestId={'admin-company-save'}
              />,
              <RingilButton
                key={'admin-company-reset'}
                mode={'outlined'}
                label={tr('generic.reset', 'Reset')}
                onClick={() => reset()}
                disabled={saving}
                dataTestId={'admin-company-reset'}
              />,
            ]}
          />

          <Tabs tabs={tabs} onTabChange={setActiveTab} />

          <div id={'admin-company-company'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <CompanySection
              form={form}
              data={data}
              conflictErrors={conflictErrors}
              setConflictErrors={setConflictErrors}
              bypassCrnValidation={bypassCrnValidation}
              setBypassCrnValidation={setBypassCrnValidation}
              bypassTinValidation={bypassTinValidation}
              setBypassTinValidation={setBypassTinValidation}
              onCheckDuplicates={(values, fields) => checkDuplicates(values, fields)}
              hasFieldChanged={hasFieldChanged}
              onSave={() => form.validateAndSend()}
              saving={saving}
            />
            <PrefixesSection form={form} setEntityPrefix={setEntityPrefix} onSave={() => form.validateAndSend()} saving={saving} />
          </div>

          <div id={'admin-company-features'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <FeaturesSection form={form} onSave={() => form.validateAndSend()} saving={saving} />
            <SectionPanel
              icon={<SvgFlag className={'w-6 h-6'} />}
              iconColor={'var(--content-tertiary)'}
              heading={tr('adminCompanyDetail.sectionBetaFeatures', 'Beta features')}
              dataTestId={'admin-company-section-beta'}>
              <BetaFeaturesSection ref={betaFeaturesRef} companyId={companyId} />
            </SectionPanel>
          </div>

          <div id={'admin-company-general'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <GeneralSection
              form={form}
              allReqs={allReqs}
              cargoTemplates={cargoTemplates}
              externalIdTypes={externalIdTypes}
              onSave={() => form.validateAndSend()}
              saving={saving}
            />
          </div>

          <div id={'admin-company-invoice'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <InvoiceSection form={form} onSave={() => form.validateAndSend()} saving={saving} />
          </div>

          <div id={'admin-company-modules'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <AdminModulesList customerId={companyId} />
            <AdminIhneritedModules customerId={companyId} />
            <AdminCompanyInheriters companyId={companyId} />
          </div>

          <div id={'admin-company-users'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <AdminCompanyUsers companyId={companyId} />
          </div>

          <div id={'admin-company-folders'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <AdminCompanyFolders companyId={companyId} />
          </div>

          <div id={'admin-company-tools'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <NotificationCenterSection companyId={companyId} />
          </div>

          <div id={'admin-company-reminders'} className={'flex flex-col gap-4 scroll-mt-[72px]'}>
            <ReminderSettingsSection companyId={companyId} />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCompanyDetailPage;
